import React, { useEffect, useState } from "react";
import { RotateCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import CampaignPanel from "./CampaignPanel";

export default function AdminEmailsTab() {
  const { t } = useI18n();
  const [emails, setEmails] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = () => api.get("/admin/emails").then(({ data }) => setEmails(data));
  useEffect(() => { load(); }, []);

  const resend = async (logId) => {
    setResendingId(logId);
    setFeedback(null);
    try {
      await api.post(`/admin/emails/${logId}/resend`);
      setFeedback({ logId, type: "success", message: t("admin.emailResendSuccess") });
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setFeedback({ logId, type: "error", message: `${t("admin.emailResendError")} ${detail}` });
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div className="space-y-3">
      <CampaignPanel />
      {feedback && (
        <p className={`rounded-xl px-4 py-2 text-sm ${feedback.type === "success" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`} data-testid="admin-email-resend-feedback">
          {feedback.message}
        </p>
      )}
      <div className="overflow-x-auto rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-4">{t("admin.emailType")}</th>
              <th className="p-4">{t("admin.emailRecipient")}</th>
              <th className="p-4">{t("admin.emailSubject")}</th>
              <th className="p-4">{t("admin.emailStatus")}</th>
              <th className="p-4">{t("admin.emailDate")}</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {emails.map((e) => (
              <tr key={e.log_id} className="border-t border-border">
                <td className="p-4 capitalize text-muted-foreground">{e.type}</td>
                <td className="p-4">{e.to}</td>
                <td className="p-4 max-w-xs truncate">{e.subject}</td>
                <td className="p-4">
                  <Badge variant={e.success ? "outline" : "secondary"} className="rounded-full">
                    {e.success ? t("admin.emailSuccess") : e.error === "smtp_not_configured" ? t("admin.emailFailedSmtp") : t("admin.emailFailed")}
                  </Badge>
                  {e.success && e.sent_via && (
                    <p className="mt-1 text-[10px] text-muted-foreground">{t("admin.emailSentVia")} {e.sent_via}</p>
                  )}
                </td>
                <td className="p-4 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</td>
                <td className="p-4">
                  {!e.success && !["reserved_test_domain_skipped", "preview_env_real_send_disabled"].includes(e.error) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      disabled={resendingId === e.log_id}
                      data-testid={ADMIN.resendEmailBtn(e.log_id)}
                      onClick={() => resend(e.log_id)}
                    >
                      <RotateCw className={`mr-1 h-3.5 w-3.5 ${resendingId === e.log_id ? "animate-spin" : ""}`} />
                      {resendingId === e.log_id ? t("admin.emailResending") : t("admin.emailResend")}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

